import { PlusCircle, Search, Edit3, Filter, Clock, UserCheck } from "lucide-react";
import { useState } from "react";
import { lmsZScore } from "../utils/lmsCalc";
import { GRADE_CFG } from "../data/clinicalConfig";

export default function ChildrenList({ children, grades, goDetail, setScreen, onEdit, user }) {
  const [search, setSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");

  const filtered = children.filter((c) => {
    // Gender filter
    if (genderFilter !== "all" && c.gender !== genderFilter) return false;
    // Grade filter
    if (gradeFilter !== "all" && (grades[c.id] ?? "Normal") !== gradeFilter) return false;
    // Search by ID, name, or block
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.nutrigridId?.toLowerCase().includes(q) ||
      c.village?.toLowerCase().includes(q)
    );
  });

  const genderLabel = (g) => g === "boys" ? "M" : "F";
  const boyCount = children.filter(c => c.gender === "boys").length;
  const girlCount = children.filter(c => c.gender === "girls").length;

  return (
    <div className="card">
      {/* Anganwadi Isolation Badge — for judges */}
      {user && (
        <div style={{
          background: "linear-gradient(135deg, #0F172A, #1E293B)", padding: "12px 20px", borderRadius: "12px 12px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              background: "#00509E", color: "#fff", padding: "4px 12px", borderRadius: 6,
              fontSize: 11, fontWeight: 800, fontFamily: "IBM Plex Mono", letterSpacing: 0.5,
            }}>
              {user.anganwadi_id || "AW-COIM-101"}
            </div>
            <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 500 }}>
              {user.role === "CDPO" ? "All Anganwadis — CDPO Access" : `Isolated to this Anganwadi only`}
            </span>
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 11.5, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>
            <span style={{ color: "#3B82F6" }}>♂ Boys: {boyCount}</span>
            <span style={{ color: "#EC4899" }}>♀ Girls: {girlCount}</span>
          </div>
        </div>
      )}

      <div className="card-header" style={{ flexWrap: "wrap", gap: 10 }}>
        <div>
          <div className="card-title">Digital Register</div>
          <div className="card-subtitle">{filtered.length} of {children.length} children · WHO LMS graded</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* NutriGrid ID / Name / Block search */}
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#7A92A8" }} />
            <input
              type="text"
              placeholder="🔎 Search NutriGrid ID / Name / Block..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "8px 12px 8px 32px", borderRadius: 8, border: "1.5px solid #D0D9E4",
                fontSize: 12.5, fontFamily: "IBM Plex Sans, sans-serif", width: 260,
                background: "#F8FAFC", outline: "none",
              }}
            />
          </div>
          {/* Gender filter */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Filter size={12} color="#7A92A8" />
            <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              style={{
                padding: "7px 10px", borderRadius: 6, border: "1.5px solid #D0D9E4",
                fontSize: 12, fontFamily: "IBM Plex Sans", background: "#F8FAFC", cursor: "pointer",
              }}
            >
              <option value="all">All Gender</option>
              <option value="boys">Boys Only</option>
              <option value="girls">Girls Only</option>
            </select>
          </div>
          {/* Grade filter */}
          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            style={{
              padding: "7px 10px", borderRadius: 6, border: "1.5px solid #D0D9E4",
              fontSize: 12, fontFamily: "IBM Plex Sans", background: "#F8FAFC", cursor: "pointer",
            }}
          >
            <option value="all">All Grades</option>
            <option value="Normal">Normal</option>
            <option value="MAM">MAM</option>
            <option value="SAM">SAM</option>
          </select>
          <button className="btn-primary" onClick={() => setScreen("add")}>
            <PlusCircle size={13} /> Register Child
          </button>
        </div>
      </div>
      <div className="table-wrap">
        <table>    
          <thead>
            <tr>
              <th>NutriGrid ID</th><th>Name</th><th>Age</th><th>Sex</th><th>Block</th>
              <th>Weight</th><th>Height</th><th>WAZ</th><th>HAZ</th><th>WHO Grade</th>
              <th>Last Edited</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((child) => {
              const last = child.records?.[child.records.length - 1];
              if (!last) return null;
              const g = grades[child.id] ?? "Normal";
              const cfg = GRADE_CFG[g];
              const sexAtBirth = child.sexAtBirth;
              const waz = lmsZScore(last.weight, last.month, child.gender, "weight", sexAtBirth);
              const haz = lmsZScore(last.height, last.month, child.gender, "height", sexAtBirth);
              return (
                <tr key={child.id}>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 11, fontWeight: 700, color: "#00509E", cursor: "pointer" }}
                    onClick={() => goDetail(child)}>
                    {child.nutrigridId || "—"}
                  </td>
                  <td onClick={() => goDetail(child)} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div className="avatar" style={{ background: cfg.dot, width: 28, height: 28, fontSize: 12 }}>{child.name?.[0]}</div>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#0D1B2A" }}>{child.name}</span>
                    </div>
                  </td>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 12 }}>{last.month}mo</td>
                  <td style={{ fontSize: 12, fontWeight: 600, color: "#3D5166" }}>
                    {genderLabel(child.gender)}
                  </td>
                  <td style={{ fontSize: 12, color: "#3D5166" }}>{child.village}</td>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 12, fontWeight: 600 }}>{last.weight} kg</td>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 12, fontWeight: 600 }}>{last.height} cm</td>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 12, fontWeight: 700, color: waz < -2 ? "#B03A2E" : "#1E8449" }}>{waz.toFixed(2)}</td>
                  <td style={{ fontFamily: "IBM Plex Mono", fontSize: 12, fontWeight: 700, color: haz < -2 ? "#B03A2E" : "#1E8449" }}>{haz.toFixed(2)}</td>
                  <td><span className={`chip ${cfg.chip}`}>{g}</span></td>
                  <td>
                    <div style={{ fontSize: 10, color: "#7A92A8", lineHeight: 1.4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <UserCheck size={10} />{last.enteredBy || "—"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                        <Clock size={9} />
                        {last.updatedAt
                          ? new Date(last.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })
                          : "—"}
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(child); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
                        borderRadius: 6, border: "1.5px solid #D0D9E4", background: "#F8FAFC",
                        cursor: "pointer", fontSize: 11.5, fontWeight: 600, color: "#00509E",
                        fontFamily: "IBM Plex Sans", transition: "all 0.15s",
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = "#EBF3FB"; e.currentTarget.style.borderColor = "#00509E"; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.borderColor = "#D0D9E4"; }}
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={12} style={{ textAlign: "center", padding: 32, color: "#7A92A8" }}>
                  No children match "{search}" {genderFilter !== "all" ? `(${genderFilter})` : ""} {gradeFilter !== "all" ? `(${gradeFilter})` : ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
